import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { Stack, Typography, ControlledInput } from '@ui-components/neo.tax';
import { useEffect, useState } from 'react';
import { Control, FormState, UseFormHandleSubmit, UseFormWatch } from 'react-hook-form';

import { useAuthErrorMessage, useDomainRedirect } from '@neo.tax/onboarding/hooks';
import {
  DomainRedirectInterstitial,
  StyledGeneralFormError,
  StyledInfoContainer,
  StyledLinkText,
  SubmitButton,
} from '@neo.tax/onboarding/organisms';
import {
  getPathUrl,
  LocalStorageKey,
  Path,
  SendMagicLinkEmailMutation,
  SendMagicLinkEmailMutationVariables,
  SEND_MAGIC_LINK_EMAIL,
  useAnalyticId,
  validateEmailAddress,
} from '@neo.tax/shared';

import { LoginFormValues } from './login-form.types';

const MAGIC_LINK_ERROR = 'There was an error sending the Magic Link. Please try again.';

export const EmailLinkForm = ({
  control,
  formState,
  handleSubmit,
  watch,
}: {
  control: Control<LoginFormValues, any>;
  handleSubmit: UseFormHandleSubmit<LoginFormValues>;
  watch: UseFormWatch<LoginFormValues>;
  formState: FormState<LoginFormValues>;
}): React.ReactElement => {
  const emailInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_EMAIL_INPUT);

  const [sendMagicLinkEmail] = useMutation<SendMagicLinkEmailMutation, SendMagicLinkEmailMutationVariables>(
    SEND_MAGIC_LINK_EMAIL,
  );
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  // The SSO buttons sit above this form and stay on screen in either login mode, so a failure they
  // report has to be displayable here too. Reading it from the same hook the password form uses keeps
  // exactly one consumer mounted per mode: without one, an SSO failure in this mode would show nothing
  // and then reappear on whichever page the user opened next.
  const [errorMessage, setErrorMessage] = useAuthErrorMessage();
  const [magicLinkDisabled, setMagicLinkDisabled] = useState<boolean>(false);

  const { isRedirecting, redirectIfSingleTenant } = useDomainRedirect();

  const [email] = watch(['email']);
  const trimmedEmail = email.trim().toLowerCase();

  useEffect(() => {
    if (magicLinkDisabled) {
      setTimeout(() => {
        setMagicLinkDisabled(false);
      }, 5000);
    }
  }, [magicLinkDisabled]);

  const onSubmit = async (): Promise<void> => {
    if (await redirectIfSingleTenant(trimmedEmail, { forwardEmail: true })) {
      return;
    }
    setIsSubmitted(true);
    setMagicLinkDisabled(true);
    try {
      await sendMagicLinkEmail({
        variables: {
          sendMagicLinkEmailInput: {
            email: trimmedEmail,
            redirectUrl: getPathUrl({ path: Path.LOGIN }),
          },
        },
      });
      localStorage.setItem(LocalStorageKey.SESSION_ONBOARDING_EMAIL, trimmedEmail);
    } catch {
      setErrorMessage(MAGIC_LINK_ERROR);
      setIsSubmitted(false);
    }
  };

  if (isRedirecting) {
    return <DomainRedirectInterstitial />;
  }

  return (
    <Stack as="form" direction="column" paddingY={6} onSubmit={handleSubmit(onSubmit)}>
      <ControlledInput
        control={control}
        name="email"
        rules={{ required: true, validate: validateEmailAddress }}
        id={emailInputId}
        label="Work Email Address"
        placeholder="Work Email Address"
      />
      {isSubmitted && !magicLinkDisabled && (
        <StyledInfoContainer>
          <Typography as="span" fontSize="sm" fontWeight="medium">
            Didn&apos;t get it?{' '}
            <button type="submit" onClick={onSubmit}>
              <StyledLinkText as="span" fontSize="sm" fontWeight="medium">
                Send it again
              </StyledLinkText>
            </button>
          </Typography>
        </StyledInfoContainer>
      )}
      <SubmitButton
        disabled={!formState.isValid || isSubmitted}
        variant="primary"
        rounded="regular"
        size="base"
        type="submit"
      >
        {isSubmitted ? 'Link Sent' : 'Send magic link'}
      </SubmitButton>
      <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
    </Stack>
  );
};
